import { apiFetch } from '@/lib/api/client';
import type {
  CompleteSessionInput,
  CompleteSessionResult,
  MurajaahQueueRow,
  MurajaahSessionStatus,
  MurajaahSessionView,
  ScheduleSessionInput,
} from '@/features/murajaah/types';
import type { PagedData } from '@/lib/api/types';

/** `GET /memorization/murajaah`. Tenant-scoped. Narrowed to Self/Child; each row individually redacted (`notes`) per LIMITED_READ. */
export async function listSessions(
  studentId: string,
  page = 1,
  limit = 20,
  status?: MurajaahSessionStatus,
): Promise<PagedData<MurajaahSessionView>> {
  const query = new URLSearchParams({ studentId, page: String(page), limit: String(limit) });
  if (status !== undefined) {
    query.set('status', status);
  }
  return apiFetch<PagedData<MurajaahSessionView>>(`/memorization/murajaah?${query.toString()}`, {
    tenantScoped: true,
  });
}

/** `GET /memorization/murajaah/:id`. Tenant-scoped. */
export async function getSession(id: string): Promise<MurajaahSessionView> {
  return apiFetch<MurajaahSessionView>(`/memorization/murajaah/${id}`, { tenantScoped: true });
}

/** `POST /memorization/murajaah`. Tenant-scoped; requires `murajaah_sessions:manage`. */
export async function scheduleSession(input: ScheduleSessionInput): Promise<MurajaahSessionView> {
  return apiFetch<MurajaahSessionView>('/memorization/murajaah', {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `DELETE /memorization/murajaah/:id`. Tenant-scoped; requires
 * `murajaah_sessions:manage`. Only a SCHEDULED session may be cancelled
 * (422 otherwise, surfaced via `ApiError.message`).
 */
export async function cancelSession(id: string): Promise<void> {
  await apiFetch<unknown>(`/memorization/murajaah/${id}`, {
    method: 'DELETE',
    tenantScoped: true,
  });
}

/**
 * `POST /memorization/murajaah/:id/complete`. Tenant-scoped; requires
 * `murajaah_sessions:manage`. Only a SCHEDULED session may be completed.
 * The returned `reviewItems` are the ONLY place this data is ever visible
 * - there is no GET endpoint to re-fetch them later.
 */
export async function completeSession(
  id: string,
  input: CompleteSessionInput,
): Promise<CompleteSessionResult> {
  return apiFetch<CompleteSessionResult>(`/memorization/murajaah/${id}/complete`, {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/**
 * `GET /memorization/murajaah/queue`. Tenant-scoped; requires
 * `murajaah_sessions:manage` (ADR-022 Addendum, Decision 7.5) - Student and
 * Parent can never reach this, by design, unrelated to Self/Child scoping.
 */
export async function listQueue(
  page = 1,
  limit = 20,
  studentId?: string,
): Promise<PagedData<MurajaahQueueRow>> {
  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (studentId !== undefined) {
    query.set('studentId', studentId);
  }
  return apiFetch<PagedData<MurajaahQueueRow>>(`/memorization/murajaah/queue?${query.toString()}`, {
    tenantScoped: true,
  });
}
